import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserPresence {
  userId: string;
  isOnline: boolean;
  lastSeen?: Date;
}

export const useUserPresence = () => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const presenceChannel = useRef<any>(null);

  useEffect(() => {
    if (!user) return;

    console.log('🟢 Setting up user presence system');
    
    // Crea il canale presence per l'intera app
    presenceChannel.current = supabase.channel('user-presence', {
      config: {
        presence: { key: user.id }
      }
    })
    .on('presence', { event: 'sync' }, () => {
      const newState = presenceChannel.current.presenceState();
      console.log('🟢 Presence sync:', newState);
      
      // Estrai tutti gli user IDs online
      const onlineUserIds = new Set<string>();
      Object.keys(newState).forEach(presenceId => {
        const presences = newState[presenceId];
        if (presences && presences.length > 0) {
          // Il presenceId dovrebbe essere l'user ID
          onlineUserIds.add(presenceId);
        }
      });
      
      setOnlineUsers(onlineUserIds);
      console.log('🟢 Online users:', Array.from(onlineUserIds));
    })
    .on('presence', { event: 'join' }, ({ key, newPresences }) => {
      console.log('🟢 User joined:', key, newPresences);
      setOnlineUsers(prev => new Set([...prev, key]));
    })
    .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
      console.log('🔴 User left:', key, leftPresences);
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    })
    .subscribe(async (status) => {
      console.log('🟢 Presence channel status:', status);
      
      if (status === 'SUBSCRIBED') {
        // Traccia la presenza dell'utente corrente
        const presenceData = {
          userId: user.id,
          timestamp: Date.now(),
          status: 'online'
        };
        
        console.log('🟢 Tracking presence for user:', user.id);
        await presenceChannel.current.track(presenceData);
      }
    });

    // Cleanup function
    return () => {
      console.log('🔴 Cleaning up presence channel');
      if (presenceChannel.current) {
        presenceChannel.current.unsubscribe();
        presenceChannel.current = null;
      }
    };
  }, [user]);

  // Funzione helper per verificare se un utente è online
  const isUserOnline = (userId: string): boolean => {
    return onlineUsers.has(userId);
  };

  // Funzione helper per ottenere tutti gli utenti online
  const getOnlineUsers = (): string[] => {
    return Array.from(onlineUsers);
  };

  return {
    onlineUsers,
    isUserOnline,
    getOnlineUsers
  };
};